// Add a route from a Komoot GPX. Choose the file; Musette measures it (distance, climb, steepest
// 500 m, moving time from your pace model), draws the map and the height, and you paint the unlit
// stretch along the profile with a finger. Then it joins the library: the planner, the walls and
// the coach see it at once. A file that matches a route already there re-measures that route.
import { useMemo, useRef, useState } from "preact/hooks";
import { useLocation } from "preact-iso";
import { useMusette, importRoute } from "../lib/store";
import { parseGpx, measure, slug, type Measured } from "../lib/gpx";
import { lightFor, litEnough, type Light } from "../lib/light";
import { movingMin, STOPS_MIN } from "../lib/ride";
import { Head } from "../components/Head";
import { RouteMap } from "../components/RouteMap";
import { useToast } from "../components/Sheet";
import { fmtDur } from "./Today";
import type { Route } from "../lib/types";
import { homeOf } from "../data/empty";

type Dark = "lit" | "marked" | "unsurveyed";

export function AddRoute() {
  const s = useMusette();
  const { route, query } = useLocation();
  const forId = (query as Record<string, string>)?.for;
  const { show, node: toast } = useToast();
  const [m, setM] = useState<Measured | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [dark, setDark] = useState<Dark>("unsurveyed");
  const [span, setSpan] = useState<[number, number] | null>(null);
  const [weeknight, setWeeknight] = useState(false);
  const [weekend, setWeekend] = useState(true);
  const [komoot, setKomoot] = useState("");
  const [busy, setBusy] = useState(false);
  const [osm, setOsm] = useState<(Light & { data: string }) | null>(null);
  const [touched, setTouched] = useState(false); // he chose the lighting himself: his survey wins
  const input = useRef<HTMLInputElement>(null);

  const read = async (f: File) => {
    setErr(null);
    const text = await f.text().catch(() => "");
    const { name: n, pts } = parseGpx(text);
    if (pts.length < 10) { setErr("That file has no route in it. In Komoot, export the route as GPX and choose that file."); return; }
    const x = measure(n, pts, homeOf(s));
    setM(x);
    const existing = s.routes.find((r) => r.id === forId) ?? s.routes.find((r) => r.id === slug(n) || r.n.toLowerCase() === n.toLowerCase());
    setName(existing?.n ?? n);
    const dur = movingMin(x.km, x.gain) + STOPS_MIN;
    setWeeknight(existing?.weeknight ?? (dur <= 150 && x.loop));
    setWeekend(existing?.weekend ?? true);
    if (existing?.u0 != null && existing.u1 != null) { setDark("marked"); setSpan([existing.u0, existing.u1]); }
    else setDark("unsurveyed");
    setKomoot(existing?.komoot ?? "");
    setTouched(false); setOsm(null);
    // Street lighting from the app's OpenStreetMap tiles, as a starting point he can change.
    const got = await lightFor(pts.map((p) => [p[0], p[1]] as [number, number]), async (path) => { try { const r = await fetch(path); return r.ok ? await r.arrayBuffer() : null; } catch { return null; } }).catch(() => null);
    setOsm(got);
    const surveyed = existing && (existing.lightFrom === "survey" || existing.surveyed || (existing.lightFrom == null && (existing.u0 != null || existing.lit)));
    if (got && !surveyed) {
      if (litEnough(got)) setDark("lit");
      else if (got.dark.length) { setDark("marked"); setSpan([got.dark[0][0], got.dark[got.dark.length - 1][1]]); }
    }
  };

  const match = useMemo(() => (m ? s.routes.find((r) => r.id === forId) ?? s.routes.find((r) => r.id === slug(name) || r.n.toLowerCase() === name.trim().toLowerCase()) : undefined), [m, name, s.routes]);
  const [asNew, setAsNew] = useState(false);
  const id = match && !asNew ? match.id : (() => { let base = slug(name), k = base, i = 2; while (s.routes.some((r) => r.id === k)) k = `${base}-${i++}`; return k; })();

  const save = async () => {
    if (!m) return;
    if (dark === "marked" && !span) { show("Paint the unlit stretch on the height, or choose Lit all the way."); return; }
    setBusy(true);
    const r: Route = {
      id, n: name.trim() || m.name, km: m.km, gain: m.gain, steep: m.steep,
      u0: dark === "marked" && span ? Math.round(span[0] * 10) / 10 : dark === "lit" ? null : match && !asNew ? match.u0 : null,
      u1: dark === "marked" && span ? Math.round(span[1] * 10) / 10 : dark === "lit" ? null : match && !asNew ? match.u1 : null,
      weeknight, weekend, ...(komoot.trim() ? { komoot: komoot.trim() } : {}),
      loop: m.loop, lit: dark === "lit", ...(m.startKmFromHome != null ? { startKm: m.startKmFromHome } : {}),
      ...(dark === "marked" ? { surveyed: new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" }) } : match && !asNew && match.surveyed ? { surveyed: match.surveyed } : {}),
      note: dark === "lit" ? (touched ? "Lit all the way." : "Lit on OpenStreetMap. Correct it if a stretch is dark.") : dark === "unsurveyed" && !(match && !asNew && match.u0 != null) ? "Lighting not surveyed yet." : touched ? match?.note : dark === "marked" ? "Unlit stretch from OpenStreetMap. Correct it after a ride." : match?.note,
      ...(touched ? { lightFrom: "survey" as const } : osm && dark !== "unsurveyed" ? { lightFrom: "osm" as const } : match && !asNew && match.lightFrom ? { lightFrom: match.lightFrom } : {}),
      ...(osm ? { osm: { lit: osm.lit, likely: osm.likely, unknown: osm.unknown, unlit: osm.unlit, data: osm.data } } : {}),
    };
    const said = await importRoute(r, { track: m.track, profile: m.profile });
    setBusy(false);
    show(said);
    route(`/routes/${id}`);
  };

  const dur = m ? movingMin(m.km, m.gain) : 0;
  return (
    <main class="screen signal">
      <Head right="Add a route" />
      <section class="stack" style="margin-top: var(--space-3)">
        <a href="/routes" class="label back">← Routes</a>
        {!m && (
          <>
            <span class="say">Bring a route in from Komoot.</span>
            <div class="card">
              <span class="soft">In Komoot, open the route, tap Share, then Export GPX, and save it to Files. Then choose it here. A file named like a route already in the library re-measures that route.</span>
              <input ref={input} type="file" hidden onChange={(e) => { const f = (e.target as HTMLInputElement).files?.[0]; if (f) read(f); }} />
              <button type="button" class="pill wide" onClick={() => input.current?.click()}>Choose a GPX file</button>
              {err && <span class="soft" role="alert">{err}</span>}
            </div>
            <span class="label">Distance by haversine, climb from heights smoothed over nine points, the same as every route in the library.</span>
          </>
        )}

        {m && (
          <>
            <div class="card">
              <span class="label">Name</span>
              <div class="composer"><input value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} aria-label="Route name" /></div>
              {match && (
                <div class="segmented" role="radiogroup" aria-label="Library">
                  <button type="button" role="radio" aria-checked={!asNew} aria-selected={!asNew} onClick={() => setAsNew(false)}>Re-measure {match.n}</button>
                  <button type="button" role="radio" aria-checked={asNew} aria-selected={asNew} onClick={() => setAsNew(true)}>Add as new</button>
                </div>
              )}
            </div>
            <div class="stats">
              <div class="card stat"><span class="label">Distance</span><span class="stat-n">{m.km}<small> km</small></span></div>
              <div class="card stat"><span class="label">Climb</span><span class="stat-n">{m.gain.toLocaleString("en-GB")}<small> m</small></span></div>
              <div class="card stat"><span class="label">Moving</span><span class="stat-n">{fmtDur(dur).replace(" h ", ":")}</span></div>
            </div>
            <div class="card map-card"><RouteMap points={m.track} label={`Map of ${name}`} h={220} tiles />
              <span class="label">{m.loop ? "A loop" : "Ends away from its start"}{m.startKmFromHome != null ? ` · starts ${m.startKmFromHome < 0.5 ? "at Meadow Mill" : `${m.startKmFromHome} km from Meadow Mill`}` : ""} · steepest 500 m {m.steep}%</span>
            </div>
            <div class="card">
              <div class="row-between"><span class="label ink">Where it goes dark</span><span class="label">{dark === "marked" && span ? `${span[0].toFixed(1)} to ${span[1].toFixed(1)} km` : dark === "lit" ? "Lit all the way" : "Not surveyed"}</span></div>
              <div class="segmented three" role="radiogroup" aria-label="Lighting">
                {([["marked", "Mark it"], ["lit", "All lit"], ["unsurveyed", "Not sure"]] as [Dark, string][]).map(([k, l]) => <button type="button" role="radio" aria-checked={dark === k} aria-selected={dark === k} onClick={() => { setDark(k); setTouched(true); }}>{l}</button>)}
              </div>
              {osm && <span class={`label${litEnough(osm) ? " ink" : osm.dark.length ? " amber" : ""}`}>OpenStreetMap · lit {osm.lit}% · 30 mph streets {osm.likely}% · not known {osm.unknown}% · unlit {osm.unlit}%{!touched && (litEnough(osm) || osm.dark.length) ? " · set from this" : ""}</span>}
              {dark === "marked" && <PaintSpan points={m.profile} span={span} onSpan={(x) => { setSpan(x); setTouched(true); }} />}
              <span class="soft">{dark === "marked" ? "Drag along the height where the street lights stop and start again. It sets the latest start on weekday evenings." : dark === "lit" ? "Any evening, any time inside your rules." : "Weekday evenings treat it with care until you mark it."}</span>
            </div>
            <div class="card">
              <span class="label">When you'd ride it</span>
              <div class="days7 two" role="group" aria-label="When">
                <button type="button" aria-pressed={weeknight} class={weeknight ? "on" : ""} onClick={() => setWeeknight(!weeknight)}>Weeknight</button>
                <button type="button" aria-pressed={weekend} class={weekend ? "on" : ""} onClick={() => setWeekend(!weekend)}>Weekend</button>
              </div>
              <span class="label">Komoot link, optional</span>
              <div class="composer"><input type="url" placeholder="https://www.komoot.com/tour/..." value={komoot} onInput={(e) => setKomoot((e.target as HTMLInputElement).value)} aria-label="Komoot link" /></div>
            </div>
            <button type="button" class="pill wide" disabled={busy || !name.trim() || (!weeknight && !weekend)} onClick={save}>{busy ? "Saving" : match && !asNew ? `Re-measure ${match.n}` : `Add ${name.trim() || "it"} to the library`}</button>
            <button type="button" class="text-btn quiet" onClick={() => { setM(null); setSpan(null); }}>Choose a different file</button>
          </>
        )}
      </section>
      <div class="dock-wrap">{toast}</div>
    </main>
  );
}

/** The height profile with a stretch you paint with a finger: down where it goes dark, up where the lights return. */
function PaintSpan({ points, span, onSpan }: { points: [number, number][]; span: [number, number] | null; onSpan: (s: [number, number]) => void }) {
  const ref = useRef<SVGSVGElement>(null);
  const a = useRef<number | null>(null);
  const W = 360, H = 120, top = 10, bottom = 16;
  const km = points[points.length - 1][0];
  const lo = Math.min(...points.map((p) => p[1])), hi = Math.max(...points.map((p) => p[1]));
  const sp = Math.max(60, hi - lo);
  const x = (k: number) => (k / km) * W, y = (m: number) => top + (1 - (m - lo) / sp) * (H - top - bottom);
  const line = points.map((p, i) => `${i ? "L" : "M"}${x(p[0]).toFixed(1)} ${y(p[1]).toFixed(1)}`).join("");
  const at = (e: PointerEvent) => { const r = ref.current!.getBoundingClientRect(); return Math.max(0, Math.min(km, ((e.clientX - r.left) / r.width) * km)); };
  return (
    <div class="profile paint">
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="slider" aria-label="Unlit stretch" aria-valuetext={span ? `${span[0].toFixed(1)} to ${span[1].toFixed(1)} km` : "Not marked"}
        onPointerDown={(e) => { (e.currentTarget as Element).setPointerCapture(e.pointerId); a.current = at(e); onSpan([a.current, a.current]); }}
        onPointerMove={(e) => { if (a.current == null) return; const b = at(e); onSpan([Math.min(a.current, b), Math.max(a.current, b)]); }}
        onPointerUp={() => { a.current = null; }}>
        {span && <rect x={x(span[0])} y={0} width={Math.max(2, x(span[1]) - x(span[0]))} height={H - bottom} class="pf-dark" />}
        <path d={`${line}L${W} ${H - bottom}L0 ${H - bottom}Z`} class="pf-area" />
        <path d={line} class="pf-line" vector-effect="non-scaling-stroke" />
      </svg>
      <div class="pf-labels"><span class="label">0</span><span class="label end">{Math.round(km)} km</span></div>
    </div>
  );
}
